/**
 * Centralized plan and entitlement model (Phase 0).
 * Single source of truth for canonical plan IDs, entitlement keys,
 * plan → entitlement mapping, and legacy Basic/Pro compatibility.
 */

// ---------------------------------------------------------------------------
// Canonical plan IDs (product plans; billing_term is separate)
// ---------------------------------------------------------------------------

export const CANONICAL_PLAN_IDS = [
  'solve_assessment',
  'casey_assessment',
  'assessments_bundle',
  'mckinsey_ai_coach_only',
  'bcg_ai_coach_only',
  'mckinsey_track_full',
  'bcg_track_full',
  'all_access',
] as const;

export type PlanId = (typeof CANONICAL_PLAN_IDS)[number];

// ---------------------------------------------------------------------------
// Entitlement keys (features unlocked by plans)
// ---------------------------------------------------------------------------

export const CANONICAL_ENTITLEMENT_KEYS = [
  'sea_wolf_solver',
  'sea_wolf_simulation',
  'red_rock_simulation',
  'mckinsey_ai_coach',
  'casey_simulation',
  'bcg_ai_coach',
] as const;

export type EntitlementKey = (typeof CANONICAL_ENTITLEMENT_KEYS)[number];

// ---------------------------------------------------------------------------
// Billing term (separate from plan_id)
// ---------------------------------------------------------------------------

export const BILLING_TERMS = ['monthly', 'six_month'] as const;

export type BillingTerm = (typeof BILLING_TERMS)[number];

// ---------------------------------------------------------------------------
// Plan → entitlements mapping
// ---------------------------------------------------------------------------

export const PLAN_TO_ENTITLEMENTS: Record<PlanId, readonly EntitlementKey[]> = {
  solve_assessment: ['sea_wolf_solver', 'sea_wolf_simulation', 'red_rock_simulation'],
  casey_assessment: ['casey_simulation'],
  assessments_bundle: [
    'sea_wolf_solver',
    'sea_wolf_simulation',
    'red_rock_simulation',
    'casey_simulation',
  ],
  mckinsey_ai_coach_only: ['mckinsey_ai_coach'],
  bcg_ai_coach_only: ['bcg_ai_coach'],
  mckinsey_track_full: [
    'sea_wolf_solver',
    'sea_wolf_simulation',
    'red_rock_simulation',
    'mckinsey_ai_coach',
  ],
  bcg_track_full: ['casey_simulation', 'bcg_ai_coach'],
  all_access: [
    'sea_wolf_solver',
    'sea_wolf_simulation',
    'red_rock_simulation',
    'mckinsey_ai_coach',
    'casey_simulation',
    'bcg_ai_coach',
  ],
};

const PLAN_IDS_SET = new Set<string>(CANONICAL_PLAN_IDS);
const ENTITLEMENT_KEYS_SET = new Set<string>(CANONICAL_ENTITLEMENT_KEYS);

/** Display names for UI (e.g. Account page) */
export const PLAN_DISPLAY_NAMES: Record<PlanId, string> = {
  solve_assessment: 'Solve Assessment',
  casey_assessment: 'Casey Assessment',
  assessments_bundle: 'Assessments Bundle',
  mckinsey_ai_coach_only: 'McKinsey AI Coach Only',
  bcg_ai_coach_only: 'BCG AI Coach Only',
  mckinsey_track_full: 'McKinsey Track (Full)',
  bcg_track_full: 'BCG Track (Full)',
  all_access: 'All-Access',
};

export function getPlanDisplayName(planId: PlanId | string | null | undefined): string {
  if (planId != null && PLAN_DISPLAY_NAMES[planId as PlanId]) {
    return PLAN_DISPLAY_NAMES[planId as PlanId];
  }
  return String(planId ?? '');
}

// ---------------------------------------------------------------------------
// Legacy mapping: Firestore/PayFast "Basic" | "Pro" → canonical plan_id
// ---------------------------------------------------------------------------

/**
 * Maps legacy plan strings (Basic, Pro) to canonical plan_id.
 * Used when subscription doc has only `plan` and no `plan_id`.
 */
export function legacyPlanToPlanId(plan: string | null | undefined): PlanId | null {
  if (plan == null) return null;
  const normalized = String(plan).trim();
  if (normalized === 'Basic' || normalized.toLowerCase() === 'basic') return 'solve_assessment';
  if (normalized === 'Pro' || normalized.toLowerCase() === 'pro') return 'all_access';
  if (PLAN_IDS_SET.has(normalized)) return normalized as PlanId;
  return null;
}

/**
 * Resolve effective plan_id from subscription data (plan_id or legacy plan).
 */
export function resolvePlanId(
  planId: string | null | undefined,
  legacyPlan: string | null | undefined
): PlanId | null {
  if (planId != null && PLAN_IDS_SET.has(String(planId))) return planId as PlanId;
  return legacyPlanToPlanId(legacyPlan);
}

// ---------------------------------------------------------------------------
// Entitlement helpers
// ---------------------------------------------------------------------------

/**
 * Returns entitlements for a canonical plan_id. Returns empty array if planId is null or unknown.
 */
export function getEntitlementsForPlanId(planId: PlanId | null): readonly EntitlementKey[] {
  if (planId == null) return [];
  const list = PLAN_TO_ENTITLEMENTS[planId as PlanId];
  return list ?? [];
}

/**
 * Check if a plan (canonical plan_id or legacy "Basic"/"Pro") unlocks the given entitlement.
 */
export function hasEntitlement(
  planIdOrLegacyPlan: PlanId | string | null | undefined,
  entitlementKey: EntitlementKey
): boolean {
  if (planIdOrLegacyPlan == null) return false;
  const planId =
    PLAN_IDS_SET.has(String(planIdOrLegacyPlan))
      ? (planIdOrLegacyPlan as PlanId)
      : legacyPlanToPlanId(planIdOrLegacyPlan as string);
  if (planId == null) return false;
  if (!ENTITLEMENT_KEYS_SET.has(entitlementKey)) return false;
  const entitlements = getEntitlementsForPlanId(planId);
  return entitlements.includes(entitlementKey);
}

/**
 * For client: derive plan_id from tier (from claims) and optional subscription doc.
 * Used when claims may not yet have plan_id but we have Firestore subscription data.
 */
export function getPlanIdFromTierAndDoc(
  tier: 'free' | 'basic' | 'pro',
  subscriptionDoc: { plan_id?: string; plan?: string } | null | undefined
): PlanId | null {
  if (subscriptionDoc?.plan_id && PLAN_IDS_SET.has(subscriptionDoc.plan_id)) {
    return subscriptionDoc.plan_id as PlanId;
  }
  if (subscriptionDoc?.plan) {
    const fromLegacy = legacyPlanToPlanId(subscriptionDoc.plan);
    if (fromLegacy) return fromLegacy;
  }
  if (tier === 'basic') return 'solve_assessment';
  if (tier === 'pro') return 'all_access';
  return null;
}
